/**
 * PmsGuests — `/pms/guests`, la cola de preparación de llegadas.
 *
 * Responde a una pregunta y a ninguna más: ¿qué llegadas NO están listas y por
 * qué? Una fila por reserva, una frase por fila, y el detalle detrás de un
 * toque. Quien ya está listo se aparta del camino.
 *
 * Son dos colas distintas puestas una al lado de la otra, no una sola:
 *   - el pre-check-in, que rellena el huésped;
 *   - el reporte de extranjeros, que presenta la propiedad.
 * Nada de una mueve a la otra (§7.4).
 */
import React, { useCallback, useMemo, useRef, useState, useEffect } from "react";
import PmsShell from "@/pages/admin/pms/PmsShell";
import { Link } from "react-router-dom";
import { ArrowSquareOut } from "@phosphor-icons/react";

import { DEMO_TODAY_ISO } from "@/data/pmsDemoData";
import { useSettingsV2, unitLabelFor } from "@/lib/settingsV2Store";
import { usePmsReservations, demoNowISO } from "@/lib/pmsRuntimeStore";
import { useGuestsV2, verifyDocument, fileImmigration } from "@/lib/guestsV2Store";
import { buildGuestParty, readinessLine, tokenForReservation } from "@/lib/guestsV2Selectors";
import { getVilla } from "@/lib/pmsSelectors";
import { SURFACE, PreviewToast, StatusPill } from "@/pages/admin/pms/v2/V2Primitives";
import GuestPartyPanel from "@/pages/admin/pms/guests/GuestPartyPanel";

/** Ventana de trabajo: lo que llega hoy o en los próximos días. */
const HORIZON_DAYS = 7;

function addDays(iso, n) {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

export default function PmsGuests() {
  const settings = useSettingsV2();
  const reservations = usePmsReservations();
  const guestState = useGuestsV2();
  const unitLabel = useMemo(() => unitLabelFor(settings.property), [settings.property]);

  const [openId, setOpenId] = useState(null);
  const [onlyPending, setOnlyPending] = useState(true);
  const [toast, setToast] = useState(null);
  const timer = useRef(null);

  const showToast = useCallback((t = "Actualizado en la vista previa") => {
    setToast(t);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(null), 2400);
  }, []);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const rows = useMemo(() => {
    const until = addDays(DEMO_TODAY_ISO, HORIZON_DAYS);
    return reservations
      .filter((r) => r.booking_status !== "cancelled")
      .filter((r) => r.check_out >= DEMO_TODAY_ISO && r.check_in <= until)
      .map((r) => buildGuestParty({ reservation: r, settings, storeState: guestState }))
      .sort((a, b) => {
        if (a.ready !== b.ready) return Number(a.ready) - Number(b.ready);
        return a.reservation.check_in.localeCompare(b.reservation.check_in);
      });
  }, [reservations, settings, guestState]);

  const pending = rows.filter((v) => !v.ready);
  const visible = onlyPending ? pending : rows;

  return (
    <PmsShell>
      <div className="mb-4">
        <div className="text-[10px] tracking-[0.3em] uppercase text-stone-500">Huéspedes</div>
        <h1 className="font-serif-dv text-2xl sm:text-3xl text-stone-900 dark:text-stone-100">
          Preparación de llegadas
        </h1>
        <div className="mt-2 flex items-center gap-3 flex-wrap">
          <span className="text-sm text-stone-600 dark:text-stone-300" data-testid="guests-count">
            {pending.length} sin preparar · {rows.length} en los próximos {HORIZON_DAYS} días
          </span>
          <button
            type="button"
            onClick={() => setOnlyPending((v) => !v)}
            aria-pressed={onlyPending}
            data-testid="guests-filter-pending"
            className={`min-h-[44px] px-3 rounded-full text-xs border ${onlyPending
              ? "border-stone-900 bg-stone-900 text-white dark:border-stone-100 dark:bg-stone-100 dark:text-stone-900"
              : "border-stone-300 dark:border-stone-700 text-stone-600 dark:text-stone-300"}`}
          >
            Solo pendientes
          </button>
        </div>
      </div>

      {visible.length === 0 ? (
        <div className={`${SURFACE} p-6 text-center text-sm text-stone-500`} data-testid="guests-empty">
          Todo preparado para las llegadas de los próximos días.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="guests-list">
          {visible.map((v) => {
            const unit = v.reservation.assigned_villa_id
              ? getVilla(v.reservation.assigned_villa_id)
              : null;
            const open = openId === v.reservation_id;
            const token = tokenForReservation(v.reservation_id);
            return (
              <li key={v.reservation_id} className={SURFACE} data-testid={`guests-row-${v.reservation_id}`}>
                <button
                  type="button"
                  onClick={() => setOpenId(open ? null : v.reservation_id)}
                  aria-expanded={open}
                  data-testid={`guests-toggle-${v.reservation_id}`}
                  className="w-full text-left px-3.5 py-2.5 min-h-[44px] flex items-start lg:items-center gap-3 flex-col lg:flex-row"
                >
                  <span className="min-w-0 lg:w-64 lg:shrink-0">
                    <span className="block text-sm text-stone-900 dark:text-stone-100 truncate">
                      {v.primary?.full_name || v.reservation.guest?.name}
                      <span className="text-stone-400"> · {unit ? unit.villa_id : `${unitLabel.singular} sin asignar`}</span>
                    </span>
                    <span className="block text-[11px] text-stone-400">
                      {v.reservation.check_in} · {v.party.declared === 1 ? "1 huésped" : `${v.party.declared} huéspedes`}
                    </span>
                  </span>
                  <span className="min-w-0 flex-1">
                    {v.ready ? (
                      <StatusPill tone="emerald" dot={false} testId={`guests-ready-${v.reservation_id}`}>
                        Lista para la llegada
                      </StatusPill>
                    ) : (
                      <span className="text-xs text-amber-700 dark:text-amber-300" data-testid={`guests-line-${v.reservation_id}`}>
                        {readinessLine(v)}
                      </span>
                    )}
                  </span>
                </button>

                {open && (
                  <div className="px-3.5 pb-3.5 border-t border-stone-100 dark:border-stone-800 pt-3">
                    <GuestPartyPanel
                      view={v}
                      onVerifyDocument={(id) => { verifyDocument(id); showToast("Documento comprobado"); }}
                      onFileImmigration={() => {
                        fileImmigration(v.reservation_id, { actor: "Dolce Admin", at: demoNowISO() });
                        showToast("Reporte marcado como presentado");
                      }}
                    />
                    <div className="mt-3 flex flex-wrap items-center gap-3">
                      <Link
                        to={`/pms/reservations/${v.reservation_id}`}
                        className="text-xs underline text-stone-600 dark:text-stone-300"
                        data-testid={`guests-open-reservation-${v.reservation_id}`}
                      >
                        Abrir la reserva
                      </Link>
                      {token && (
                        <Link
                          to={`/guest/${token}`}
                          className="text-xs underline text-stone-600 dark:text-stone-300 inline-flex items-center gap-1"
                          data-testid={`guests-open-link-${v.reservation_id}`}
                        >
                          Ver el portal del huésped <ArrowSquareOut size={11} />
                        </Link>
                      )}
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <PreviewToast text={toast} testId="guests-toast" />
    </PmsShell>
  );
}
